import React, { useEffect, useMemo, useRef, useState } from 'react';
import ModalComponent from '../ModalComponent';
import { useTranslations } from '../context/TranslationContext';
import Button from '../legacy/core/Button';
import AlpacaForm from '../AlpacaForm';
import fetchCustom, { callLinkAction } from '../../wp_helpers/fetchCustom';
import { notification } from '../../utils/utils';
import {
    convertFormDataToSchema,
    convertSchemaToFormData,
    getAnsweredQuestionsBeingRemoved,
    getAnsweredQuestionsBeingReplaced,
} from './utils';
import getResponseJson from '@helpers/getResponseJson';
import { appendQueryParams } from '@helpers/helpers';
import './VideoQuestionsEditor.less';

const VideoQuestionsEditor = ({
    jobId,
    showModal,
    onCloseModal,
    onSave,
    type,
    stepId,
}) => {
    const { t, updateTranslations } = useTranslations();
    const formRef = useRef(null);

    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const getFormData = async () => {
        return formRef.current.getFormData();
    };

    const fetchData = async () => {
        setLoading(true);
        setIsDirty(false);
        try {
            const url = appendQueryParams(`/api/jobs/${jobId}/ui`, { expand: type, step_id: stepId });
            const response = await fetchCustom(url);
            const responseJson = await getResponseJson(response);
            if (response.ok) {
                const { [type]: _data = {} } = responseJson || {};
                setData(_data);
                updateTranslations(_data?.translations || {});
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, [jobId, updateTranslations, type, stepId, showModal]);

    const getMetadataApi = data?._links?.get_metadata?.url;
    const canSave = !!data?._links?.put?.url;

    const formSchema = useMemo(() => {
        const _schema = JSON.parse(data?.metadata || '{}');
        const videoQuestionsFormData = convertSchemaToFormData(data?.form);
        const baseData =
            _schema.data && typeof _schema.data === 'object' && !Array.isArray(_schema.data)
                ? { ..._schema.data }
                : {};
        if (data?.interview_deadline != null) {
            baseData.interview_deadline = data.interview_deadline;
        }
        _schema.data = {
            ...baseData,
            ...videoQuestionsFormData,
        };
        return _schema;
    }, [data]);

    const title = data?.name;
    const modalDescription = data?.description;

    const saveForm = async (form) => {
        if (!canSave) return;
        const response = await callLinkAction(data?._links, 'put', { body: JSON.stringify(form) });
        const responseJson = await getResponseJson(response);
        if (!response.ok) {
            const errorMsg = responseJson?.notification || t('Failed to save. Please try again.');
            throw new Error(errorMsg);
        }
        notification(t('Saved successfully'), { type: 'success' });
        onSave?.(responseJson?.[type] || {});
    };

    const handleSaveForm = async () => {
        setSaving(true);
        const formContainer = formRef.current.getContainer();
        formContainer.refreshValidationState(true, async () => {
            if (!formContainer.isValid(true)) {
                formContainer.focus();
                notification(t('Please fix the errors and try again.'));
                setSaving(false);
                return;
            }
            try {
                const _formData = await getFormData();
                const form = {
                    ..._formData,
                    form: {
                        ...await convertFormDataToSchema(_formData),
                        data_sources: {
                            video_recorder_metadata_url: getMetadataApi,
                        },
                    },
                };
                delete form.video_questions_section;

                // Warn before dropping a question candidates already answered. The
                // answer is not deleted -- it stays in the field table and stops being
                // rendered -- so the count on the application falls with nothing on
                // screen to explain it unless we say so here.
                const answeredQuestionsBeingRemoved = getAnsweredQuestionsBeingRemoved(data?.form, form.form);
                const removalConfirmed = answeredQuestionsBeingRemoved.every((question) => window.confirm(
                    t('video_question_removed_warning', {
                        title: question.title,
                        count: question.answeredCount,
                        __count: question.answeredCount,
                    })
                ));
                if (!removalConfirmed) {
                    setSaving(false);
                    return;
                }

                // Warn before replacing a question candidates already
                // answered — their answers are kept (and flagged), never lost,
                // but the recruiter should know before confirming the change.
                const answeredQuestionsBeingReplaced = getAnsweredQuestionsBeingReplaced(data?.form, form.form);
                const confirmed = answeredQuestionsBeingReplaced.every((question) => window.confirm(
                    t('video_question_answered_warning', {
                        count: question.answeredCount,
                        __count: question.answeredCount,
                    })
                ));
                if (!confirmed) {
                    setSaving(false);
                    return;
                }

                await saveForm(form);
                setIsDirty(false);
                await handleCloseModal();
            } catch (err) {
                console.error('Error saving video questions:', err);
                notification(err.message || t('Failed to save. Please try again.'));
            } finally {
                setSaving(false);
            }
        });
    };

    const handleCloseModal = async () => {
        onCloseModal();
        return true;
    };

    return (
        <React.Fragment>
            <ModalComponent
                show={showModal}
                closeModal={handleCloseModal}
                closeModalConfirmationMessage={isDirty ? t('Are you sure you want to close the dialogue? All changes will be lost.') : undefined}
            >
                <div className="overlay overlay-w-735">
                    <div className="overlay-header">
                        <h3>{title || t('Video questions')}</h3>
                        {modalDescription && <p>{modalDescription}</p>}
                    </div>
                    <div className="overlay-main">
                        {!loading && formSchema.schema &&
                            <AlpacaForm
                                ref={formRef}
                                formId={'video-questions-editor-form'}
                                formSchema={formSchema}
                                onChange={() => setIsDirty(true)}
                            />
                        }
                        {loading && t("Fetching data...")}
                    </div>
                    {canSave && <div className="overlay-footer">
                        <div className="btn-container">
                            <Button type="primary" disabled={loading || saving} onClick={handleSaveForm} isLink>{t('Save')}</Button>
                        </div>
                    </div>}
                </div>
            </ModalComponent>
        </React.Fragment>
    );
};

export default VideoQuestionsEditor;
